// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import { api } from '@api/client';
import type {
  ExternalServiceConfigItem,
  ExternalServiceEndpointConfig,
  ExternalServicesConfig,
} from '@/types/externalServices';
import type {
  ModelConfigLocalStore,
  ProviderModelConfigItem,
  ProviderModelConfigFiveTuple,
} from '@/types/modelConfig';
import { DEFAULT_TEMPERATURE, DEFAULT_MAX_TOKENS } from '@/config/modelDefaults';

/**
 * external-services -> provider model-config 一次性迁移
 *
 * 迁移背景：
 * - 旧入口：设置页里的 "external-services（模型/外部服务）配置"（ModelConfigManager）
 * - 新入口：模型供应商中心，使用 provider_code -> 五元组（base_url/api_key/model_name/temperature/max_tokens）
 *
 * 迁移目标：
 * 1) 从 /api/v1/config/external-services 读取旧配置
 * 2) 将可识别的"模型服务"（存在 endpoint + model 的模式）转换为新五元组
 * 3) 写入：
 *    - localStorage（用于秒开/多 Tab 同步/保留明文 api_key 的可选体验）
 *    - 后端 /api/model-config（用于跨设备持久化；GET 会脱敏 api_key）
 * 4) 幂等：使用 localStorage flag，保证只迁移一次（避免重复覆盖用户新配置）
 */

// ── Usage evidence (deletion-gate mechanism) ────────────────────
/** One-shot evidence emitted when the migration function executes (or is skipped via flag). */
export type MigrationEvidence = {
  /** Whether the migration body actually executed (false = short-circuited by done flag). */
  ran: boolean;
  /** Whether the external-services config endpoint returned any non-empty services. */
  hadLegacyConfig: boolean;
  /** Number of provider_codes that were migrated or merged in this call. */
  migratedCount: number;
};

let _migrationEvidenceSink: ((e: MigrationEvidence) => void) | null = null;

/**
 * Install a one-shot evidence sink for deletion-gate decisions.
 * Call with null to remove. The sink is fired at most once per call to
 * `migrateExternalServicesToProviderModelConfigOnce`.
 */
export function setMigrationEvidenceSink(
  fn: ((e: MigrationEvidence) => void) | null
): void {
  _migrationEvidenceSink = fn;
}

/** 新模型供应商配置 localStorage key。 */
export const PROVIDER_MODEL_CONFIG_LOCAL_STORAGE_KEY = 'provider-model:configs-v1';
/** 兼容旧版本本地 key，仅用于一次性读取和迁移。 */
export const LEGACY_PROVIDER_MODEL_CONFIG_LOCAL_STORAGE_KEY = 'agent-flow:model-configs-v1';

/**
 * 迁移幂等标记：
 * - 一旦写入 done=true，就不再重复触发迁移
 * - 该标记是“前端一次性迁移”的实现细节（不与后端强绑定）
 */
export const MODEL_CONFIG_MIGRATION_FLAG_KEY =
  'provider-model:migration:external-services->model-config-v1';

export type ModelConfigMigrationResult = {
  /** 本次调用是否实际执行了迁移逻辑（没被 flag 短路） */
  ran: boolean;
  /** external-services 是否存在任何 services（用于判断要不要弹“入口迁移提示”） */
  hadLegacyConfig: boolean;
  /** 成功写入（或合并更新）到新结构的 provider_code 列表 */
  migratedProviderCodes: string[];
  /** 因缺失字段/已存在等原因跳过的 service_id（用于 debug） */
  skippedServiceIds: string[];
};

type MigrationFlagV1 = {
  version: '1';
  done: boolean;
  done_at: number;
  migrated_count: number;
};

function safeNow() {
  return Date.now();
}

function safeParseJson<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function readMigrationFlag(): MigrationFlagV1 | null {
  const flag = safeParseJson<MigrationFlagV1>(
    window.localStorage.getItem(MODEL_CONFIG_MIGRATION_FLAG_KEY)
  );
  if (!flag) return null;
  if (flag.version !== '1') return null;
  if (typeof flag.done !== 'boolean') return null;
  if (typeof flag.done_at !== 'number') return null;
  if (typeof flag.migrated_count !== 'number') return null;
  return flag;
}

function writeMigrationFlag(flag: MigrationFlagV1) {
  window.localStorage.setItem(MODEL_CONFIG_MIGRATION_FLAG_KEY, JSON.stringify(flag));
}

/**
 * 解析 provider model-config localStorage（最小校验，避免读取到脏数据导致崩溃）
 */
function safeParseProviderModelLocalStore(raw: string | null): ModelConfigLocalStore | null {
  const parsed = safeParseJson<unknown>(raw);
  if (!parsed || typeof parsed !== 'object') return null;
  const obj = parsed as Partial<ModelConfigLocalStore>;
  if (obj.version !== '1') return null;
  if (!obj.items || typeof obj.items !== 'object') return null;
  if (typeof obj.updated_at !== 'number') return null;
  return obj as ModelConfigLocalStore;
}

function derivePublicFromApiKey(api_key?: string) {
  const key = (api_key || '').trim();
  return {
    has_api_key: Boolean(key),
    api_key_last4: key ? key.slice(-4) : null,
  };
}

function coerceNumber(value: unknown, fallback: number) {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * 选择一个“最可能正确”的 mode：
 * - 优先 default_mode（如果存在）
 * - 其次选择 enabled=true 的任意模式
 * - 再次 cloud/local
 * - 最后回退到第一个 mode
 */
function pickPreferredMode(service: ExternalServiceConfigItem): string | null {
  const modes = service?.modes || {};
  const modeKeys = Object.keys(modes);
  if (modeKeys.length === 0) return null;

  const preferred: string[] = [];

  if (service.default_mode && typeof service.default_mode === 'string')
    preferred.push(service.default_mode);

  // enabled=true 的 mode（稳定排序：字典序）
  const enabledModes = modeKeys
    .filter((k) => (modes[k] as ExternalServiceEndpointConfig | undefined)?.enabled === true)
    .sort((a, b) => a.localeCompare(b));
  preferred.push(...enabledModes);

  // 常见 key
  preferred.push('cloud', 'local');

  // 最终兜底：所有 mode
  preferred.push(...modeKeys);

  for (const key of preferred) {
    if (modes[key]) return key;
  }
  return modeKeys[0] || null;
}

/**
 * provider_code 生成策略（兼顾”尽量可测试”与”避免 key 冲突”）：
 * - 若 service_id 本身就是已知供应商前缀：直接沿用
 * - 若 mode.type 明确：使用 `${type}-${service_id}` 前缀
 * - 其余情况：直接使用 service_id（唯一且可追溯）
 */
function deriveProviderCode(serviceId: string, _modeType?: string) {
  const sid = (serviceId || '').trim().toLowerCase();
  if (!sid) return '';
  return sid;
}

function toFiveTuple(input: {
  endpoint: string;
  api_key?: string;
  model_name: string;
  temperature?: unknown;
  max_tokens?: unknown;
}): ProviderModelConfigFiveTuple {
  return {
    base_url: (input.endpoint || '').trim().replace(/\/+$/, ''),
    ...(input.api_key ? { api_key: input.api_key } : {}),
    model_name: (input.model_name || '').trim(),
    temperature: coerceNumber(input.temperature, DEFAULT_TEMPERATURE),
    max_tokens: Math.trunc(coerceNumber(input.max_tokens, DEFAULT_MAX_TOKENS)),
  };
}

function createProviderItem(
  provider_code: string,
  config: ProviderModelConfigFiveTuple
): ProviderModelConfigItem {
  // ADR-025: derive public metadata from api_key but don't store api_key
  const publicView = derivePublicFromApiKey(config.api_key);
  const { api_key: _, ...configWithoutKey } = config;
  void _;
  return {
    provider_code,
    config: configWithoutKey as ProviderModelConfigFiveTuple,
    public: publicView,
    tested: { tested_ok: false, tested_at: null, tested_summary: null },
    favorite: false,
  };
}

/**
 * 执行一次性迁移（幂等）。
 *
 * 注意：
 * - 该函数不会“强覆盖”用户已经在新面板里配置过的 provider_code；
 *   - 若已存在，则仅做“补全缺失字段”（例如旧配置里带 api_key，而新配置没填）
 * - 为了避免迁移失败导致无限重试，这里会在“完成一次迁移尝试”后写入 done 标记；
 *   - 若需要重新迁移，可手动清理 localStorage 的 MODEL_CONFIG_MIGRATION_FLAG_KEY
 */
export async function migrateExternalServicesToProviderModelConfigOnce(): Promise<ModelConfigMigrationResult> {
  const flag = readMigrationFlag();
  if (flag?.done) {
    _migrationEvidenceSink?.({
      ran: false,
      hadLegacyConfig: false,
      migratedCount: 0,
    });
    return {
      ran: false,
      hadLegacyConfig: false,
      migratedProviderCodes: [],
      skippedServiceIds: [],
    };
  }

  const migratedProviderCodes: string[] = [];
  const skippedServiceIds: string[] = [];

  // 1) 读取旧 external-services 配置
  let legacy: ExternalServicesConfig | null = null;
  try {
    const res = await api.getExternalServicesConfig();
    legacy = (res.data || null) as ExternalServicesConfig | null;
  } catch {
    legacy = null;
  }

  const legacyServices = legacy?.services || {};
  const hadLegacyConfig = Object.keys(legacyServices).length > 0;

  // 2) 读取新结构（用于 merge，而非覆盖）
  const existingLocal =
    safeParseProviderModelLocalStore(
      window.localStorage.getItem(PROVIDER_MODEL_CONFIG_LOCAL_STORAGE_KEY)
    ) ||
    safeParseProviderModelLocalStore(
      window.localStorage.getItem(LEGACY_PROVIDER_MODEL_CONFIG_LOCAL_STORAGE_KEY)
    );
  const existingItems = existingLocal?.items || {};

  // ADR-025: strip api_key from existing local items (one-time cleanup)
  const nextItems: Record<string, ProviderModelConfigItem> = {};
  for (const [code, item] of Object.entries(existingItems)) {
    const { api_key: _, ...configWithoutKey } = item.config;
    void _;
    nextItems[code] = {
      ...item,
      config: configWithoutKey as ProviderModelConfigFiveTuple,
      public: item.public || derivePublicFromApiKey(item.config.api_key),
    };
  }
  const providerCodesToUpsert: string[] = [];

  for (const [serviceId, service] of Object.entries(legacyServices)) {
    const modeKey = pickPreferredMode(service);
    if (!modeKey) {
      skippedServiceIds.push(serviceId);
      continue;
    }

    const mode = service.modes?.[modeKey];
    const endpoint = typeof mode?.endpoint === 'string' ? mode.endpoint.trim() : '';
    const modelName = typeof mode?.model === 'string' ? mode.model.trim() : '';

    // 仅迁移“像模型服务”的条目：必须有 endpoint + model
    if (!endpoint || !modelName) {
      skippedServiceIds.push(serviceId);
      continue;
    }

    const provider_code = deriveProviderCode(serviceId, mode?.type);
    if (!provider_code) {
      skippedServiceIds.push(serviceId);
      continue;
    }

    const apiKey =
      typeof (mode as any)?.api_key === 'string' ? String((mode as any).api_key).trim() : '';
    const params = (mode as any)?.parameters;
    const fiveTuple = toFiveTuple({
      endpoint,
      api_key: apiKey || undefined,
      model_name: modelName,
      temperature: params?.temperature,
      max_tokens: params?.max_tokens,
    });

    // 若关键字段缺失（空字符串），跳过（避免写入坏配置导致新面板报错）
    if (!fiveTuple.base_url || !fiveTuple.model_name) {
      skippedServiceIds.push(serviceId);
      continue;
    }

    const existing = nextItems[provider_code];
    if (!existing) {
      nextItems[provider_code] = createProviderItem(provider_code, fiveTuple);
      providerCodesToUpsert.push(provider_code);
      migratedProviderCodes.push(provider_code);
      continue;
    }

    // 已存在：仅补全”缺失字段”，避免覆盖用户在新面板里刚配置好的值
    const mergedConfig: ProviderModelConfigFiveTuple = {
      base_url: existing.config.base_url || fiveTuple.base_url,
      model_name: existing.config.model_name || fiveTuple.model_name,
      temperature: Number.isFinite(Number(existing.config.temperature))
        ? existing.config.temperature
        : fiveTuple.temperature,
      max_tokens: Number.isFinite(Number(existing.config.max_tokens))
        ? existing.config.max_tokens
        : fiveTuple.max_tokens,
    };

    // 若 merge 后与原始不同，才视为”迁移更新”
    const changed =
      mergedConfig.base_url !== existing.config.base_url ||
      mergedConfig.model_name !== existing.config.model_name ||
      mergedConfig.temperature !== existing.config.temperature ||
      mergedConfig.max_tokens !== existing.config.max_tokens;

    if (changed) {
      const mergedPublic = derivePublicFromApiKey(fiveTuple.api_key);
      nextItems[provider_code] = {
        ...existing,
        config: mergedConfig,
        public: existing.public.has_api_key ? existing.public : mergedPublic,
      };
      providerCodesToUpsert.push(provider_code);
      migratedProviderCodes.push(provider_code);
    }
  }

  // 3) Write to localStorage: always write if we had existing items (to strip legacy api_key)
  const hadApiKeyToStrip = Object.values(existingItems).some(
    (item) => item.config.api_key
  );
  const hasAnyChange = migratedProviderCodes.length > 0 || hadApiKeyToStrip;

  if (hasAnyChange) {
    const nextLocal: ModelConfigLocalStore = {
      version: '1',
      updated_at: safeNow(),
      items: nextItems,
    };
    window.localStorage.setItem(PROVIDER_MODEL_CONFIG_LOCAL_STORAGE_KEY, JSON.stringify(nextLocal));
  }

  // 4) 写入后端 /api/model-config（逐个 upsert；失败不阻断整体迁移）
  // api_key is sent to the server for secure storage (ADR-025)
  if (providerCodesToUpsert.length > 0) {
    await Promise.allSettled(
      providerCodesToUpsert.map(async (provider_code) => {
        const item = nextItems[provider_code];
        if (!item) return;
        const cfg = item.config;
        // Collect api_key from the original fiveTuple for this provider
        // We need to re-extract it since we don't store it in nextItems
        const legacyService = legacyServices[provider_code] || legacyServices[Object.keys(legacyServices).find(k => k.toLowerCase() === provider_code) || ''];
        let apiKey = '';
        if (legacyService) {
          const modeKey = pickPreferredMode(legacyService);
          const mode = modeKey ? legacyService.modes?.[modeKey] : null;
          apiKey = typeof (mode as any)?.api_key === 'string' ? String((mode as any).api_key).trim() : '';
        }
        await api.upsertModelConfig(
          {
            provider_code,
            config: {
              base_url: cfg.base_url,
              model_name: cfg.model_name,
              temperature: cfg.temperature,
              max_tokens: cfg.max_tokens,
              api_key: apiKey,
            },
          },
          // G7-L: 非 admin 的 403 是预期路径（迁移是管理员侧一次性动作）——
          // 静默容忍不弹全局 toast；allSettled 吞单点失败，幂等 flag 照常写入。
          { suppressErrorToast: true }
        );
      })
    );
  }

  // 5) 写入幂等标记：避免反复迁移
  writeMigrationFlag({
    version: '1',
    done: true,
    done_at: safeNow(),
    migrated_count: migratedProviderCodes.length,
  });

  _migrationEvidenceSink?.({
    ran: true,
    hadLegacyConfig,
    migratedCount: migratedProviderCodes.length,
  });

  return {
    ran: true,
    hadLegacyConfig,
    migratedProviderCodes,
    skippedServiceIds,
  };
}
